import {
  documentCookies,
  isSecurePage,
  onPageReturn,
  type CookieJar,
  type OnPageReturn,
} from './browser';
import {
  SIGNED_OUT_MARKER,
  SSO_SESSION_MAX_AGE_SECONDS,
  SSO_SESSION_PATH,
  SSO_TOKEN_PATH,
  ssoCookieNames,
  type SsoSessionJoined,
  type SsoSessionStarted,
} from './protocol';

const REQUEST_TIMEOUT_MS = 10_000;

/** This app's Firebase sign-in, whichever version of the Firebase SDK it uses. */
export interface SsoSession {
  /** The signed-in user's uid, or null. */
  currentUid(): string | null;
  /** A current ID token for the signed-in user, or null when nobody is signed in. */
  idToken(): Promise<string | null>;
  signInWithCustomToken(customToken: string): Promise<void>;
  signOut(): Promise<void>;
}

/**
 * `network`: the API couldn't be reached. `refused`: it answered with an error status.
 * `unexpected`: anything else, such as Firebase refusing the custom token.
 */
export type SsoErrorKind = 'network' | 'refused' | 'unexpected';

export class SsoError extends Error {
  constructor(
    readonly kind: SsoErrorKind,
    message: string,
    /** The HTTP status, for `refused`. */
    readonly status: number | null = null,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'SsoError';
  }
}

export interface SsoClientOptions {
  /** Estimating's API for the brand and environment, such as `https://estimating.imex.online`. */
  readonly apiBaseUrl: string;
  /** This app's Firebase project, the brand's for this environment. */
  readonly projectId: string;
  /**
   * The domain the API sets the cookies on for this environment, such as `imex.online`
   * or `test.imex.online`; null when they belong to the API's host alone, as locally.
   * With the project, it names the cookies, so it must match the API's setting.
   */
  readonly cookieDomain: string | null;
  readonly session: SsoSession;
  /** Failures, which the next sync retries; for logging. */
  readonly onError?: (error: SsoError) => void;
  // The rest replace browser features in tests.
  readonly fetch?: (url: string, init: RequestInit) => Promise<Response>;
  readonly cookies?: CookieJar;
  readonly onPageReturn?: OnPageReturn;
  /** Defaults to whether the page is served over HTTPS. */
  readonly secure?: boolean;
}

export interface SsoClient {
  /**
   * Brings this app in line with the browser's shared sign-in: joins it, shares this
   * app's own, or signs out when someone signed out elsewhere. Call it once Firebase has
   * restored the session from an earlier visit. Never rejects; failures go to `onError`.
   */
  sync(): Promise<void>;
  /**
   * Runs this app's own sign-in, such as `signInWithEmailAndPassword`, and then shares
   * it, so the other app signs in as the same person. Resolves or rejects as the sign-in
   * does, without waiting for the sharing.
   */
  signIn<T>(signInHere: () => Promise<T>): Promise<T>;
  /**
   * Ends the shared sign-in, so the other app signs out too, then signs out of this one.
   * Use it instead of Firebase's `signOut`. Rejects only if Firebase's sign-out fails.
   */
  signOut(): Promise<void>;
  /** Syncs whenever the person comes back to the page. Returns a function that stops. */
  watch(): () => void;
}

function hasStrings<K extends string>(
  body: unknown,
  keys: readonly K[],
): body is Record<K, string> {
  return (
    typeof body === 'object' &&
    body !== null &&
    keys.every((key) => typeof (body as Record<string, unknown>)[key] === 'string')
  );
}

/** The API's error code, when the body is its usual `ApiError` shape. */
async function errorCode(response: Response): Promise<string> {
  try {
    const body: unknown = await response.json();
    return hasStrings(body, ['code']) ? body.code : 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Single sign-on between apps that share a brand's Firebase project, on hosts of one
 * domain (D-060). Each app keeps its own Firebase session; estimating's API holds a
 * shared sign-in in cookies on that domain, and a marker cookie the apps can read tells
 * them when it changes. The newest sign-in or sign-out in the browser, in either app, wins.
 */
export function createSsoClient(options: SsoClientOptions): SsoClient {
  const { session, cookieDomain, onError = () => undefined } = options;
  const secure = options.secure ?? isSecurePage();
  const names = ssoCookieNames({ projectId: options.projectId, cookieDomain, secure });
  const cookies = options.cookies ?? documentCookies();
  const send = options.fetch ?? ((url, init) => fetch(url, init));
  const apiBaseUrl = options.apiBaseUrl.replace(/\/+$/, '');

  // The marker this page has acted on, so a sync with nothing new sends no request.
  // Held in memory: after a reload the client checks with the API once.
  let synced: string | null | undefined;
  // This page's own sign-in, not shared yet because the API couldn't be reached. It is
  // the newest, so the next sync shares it rather than act on an older shared state.
  let unshared = false;
  // One operation at a time, so a sync can't interleave with a sign-in or sign-out.
  let tail: Promise<unknown> = Promise.resolve();

  function enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const result = tail.then(operation);
    tail = result.catch(() => undefined);
    return result;
  }

  function unexpected(message: string, cause?: unknown): void {
    onError(new SsoError('unexpected', message, null, { cause }));
  }

  /** Resolves undefined after a failure it has reported. */
  async function call(
    method: 'POST' | 'DELETE',
    path: string,
    idToken: string | null = null,
  ): Promise<Response | undefined> {
    let response: Response;
    try {
      response = await send(`${apiBaseUrl}${path}`, {
        method,
        // The session cookie goes to the API even when it is on another host.
        credentials: 'include',
        cache: 'no-store',
        headers: idToken === null ? {} : { authorization: `Bearer ${idToken}` },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
    } catch (error) {
      onError(new SsoError('network', `${method} ${path} failed`, null, { cause: error }));
      return undefined;
    }
    // 401 from the token route only means there is nothing to join; its caller decides.
    if (!response.ok && !(path === SSO_TOKEN_PATH && response.status === 401)) {
      const message = `${method} ${path} answered ${response.status} ${await errorCode(response)}`;
      onError(new SsoError('refused', message, response.status));
      return undefined;
    }
    return response;
  }

  /** Starts a shared sign-in from this app's own. */
  async function share(): Promise<void> {
    const idToken = await session.idToken();
    const response = idToken === null ? undefined : await call('POST', SSO_SESSION_PATH, idToken);
    if (response === undefined) {
      return;
    }
    const started: unknown = await response.json();
    if (!hasStrings<keyof SsoSessionStarted>(started, ['marker'])) {
      unexpected(`POST ${SSO_SESSION_PATH} answered with an unexpected body`);
      return;
    }
    synced = started.marker;
    unshared = false;
  }

  /** Joins the shared sign-in, as another user if need be. */
  async function join(uid: string | null): Promise<void> {
    const response = await call('POST', SSO_TOKEN_PATH);
    if (response === undefined) {
      return;
    }
    if (response.status === 401) {
      // It expired or was revoked, and the API has cleared its cookies.
      synced = null;
      if (uid !== null) {
        await share();
      }
      return;
    }
    const joined: unknown = await response.json();
    if (!hasStrings<keyof SsoSessionJoined>(joined, ['marker', 'uid', 'customToken'])) {
      unexpected(`POST ${SSO_TOKEN_PATH} answered with an unexpected body`);
      return;
    }
    if (joined.uid !== uid) {
      await session.signInWithCustomToken(joined.customToken);
    }
    synced = joined.marker;
  }

  async function reconcile(): Promise<void> {
    const uid = session.currentUid();
    if (unshared && uid !== null) {
      await share();
      return;
    }
    const marker = cookies.read(names.marker);
    if (marker === synced) {
      return;
    }
    if (marker === SIGNED_OUT_MARKER) {
      if (uid !== null) {
        await session.signOut();
      }
      synced = marker;
    } else if (marker === null) {
      // No shared sign-in, or it expired: share this app's, if it has one.
      if (uid === null) {
        synced = null;
      } else {
        await share();
      }
    } else {
      await join(uid);
    }
  }

  function sync(): Promise<void> {
    return enqueue(async () => {
      try {
        await reconcile();
      } catch (error) {
        unexpected('Syncing failed', error);
      }
    });
  }

  function signIn<T>(signInHere: () => Promise<T>): Promise<T> {
    const signedIn = enqueue(signInHere);
    // Queued straight after the sign-in, so no sync can come between them.
    void enqueue(async () => {
      try {
        await signedIn;
      } catch {
        return;
      }
      unshared = true;
      try {
        await share();
      } catch (error) {
        unexpected('Sharing the sign-in failed', error);
      }
    });
    return signedIn;
  }

  function signOut(): Promise<void> {
    return enqueue(async () => {
      if ((await call('DELETE', SSO_SESSION_PATH)) === undefined) {
        // The API couldn't end it, so mark it ended here; then no app joins it again.
        cookies.write(names.marker, SIGNED_OUT_MARKER, {
          domain: cookieDomain,
          maxAgeSeconds: SSO_SESSION_MAX_AGE_SECONDS,
          secure,
        });
      }
      synced = SIGNED_OUT_MARKER;
      unshared = false;
      await session.signOut();
    });
  }

  function watch(): () => void {
    return (options.onPageReturn ?? onPageReturn)(() => {
      void sync();
    });
  }

  return { sync, signIn, signOut, watch };
}
